import type { QueryClient } from '@tanstack/react-query';

import { getCartQuery } from '@/api/booking/getCartQuery';
import type { ShopDetail } from '@/api/shop/shopDetailQuery';
import { getCartServiceIds } from '@/utils/booking/getCartServiceIds';
import { getL1CategoriesWithSubcategories } from '@/utils/booking/getL1CategoriesWithSubcategories';
import { peekCategoryData } from '@/utils/booking/peekCategoryData';

interface HasSingleVisibleL1Params {
  queryClient: QueryClient;
  /** Undefined while the shop is still loading. */
  shop: Pick<ShopDetail, 'id' | 'brandId'> | undefined;
  /** Undefined means "any professional". */
  barberIdOrRoute?: string;
  cartId?: string;
}

/**
 * Whether the L1 list would show exactly one L1, and so redirect past itself.
 * Cached data only: a missing entry means "not known" and returns false.
 */
export function hasSingleVisibleL1({
  queryClient,
  shop,
  barberIdOrRoute,
  cartId,
}: HasSingleVisibleL1Params): boolean {
  const data = peekCategoryData({ queryClient, shop, barberIdOrRoute });
  if (!shop || !data) return false;
  const { barberId, rows, services } = data;

  let cart = null;
  if (cartId) {
    cart = queryClient.getQueryData(getCartQuery(shop.id, cartId).queryKey);
    if (!cart) return false;
  }

  const cartServiceIds = getCartServiceIds(cart);
  return (
    getL1CategoriesWithSubcategories({
      rows,
      services,
      // A named professional is not narrowed by the cart.
      cartServices: barberId
        ? []
        : services.filter(({ id }) => cartServiceIds.includes(id)),
      requireBarberAssignment: !barberId,
    }).length === 1
  );
}
